import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { Card } from 'pokemon-tcg-sdk-typescript';
import { Grid, List, Book, ArrowUpDown, ArrowUp, ArrowDown, Hash, DollarSign, Calendar, Sparkles, Search, Zap, Sword, Shield } from 'lucide-react';
import LoadingHeader from './LoadingHeader';
import { EnergyIcon } from './icons/EnergyIcon';
import CollectionButton, { CardFinish } from './CollectionButton';
import { HPIcon } from './icons/HPIcon'; 
import { useCurrencyStore } from '../lib/currencyStore';
import { CardGridSkeleton } from './skeletons/CardSkeleton';
import { formatPrice } from '../utils/formatPrice';
import { useScroll } from 'framer-motion';
import { slugify } from '../utils/slugify';
import CardImage from './CardImage';
import { getCardPrice } from '../utils/cardPricing';
import { useBatchCardFinishes } from '../hooks/useBatchCardFinishes';

interface CardGridProps {
  cards: Card[];
  onCardClick: (card: Card) => void;
  activeFilter?: string;
  getCardKey?: (card: Card) => string;
  loading?: boolean;
  isCollectionView?: boolean;
  enablePagination?: boolean;
  cardsPerPage?: number;
}

interface CardObserverProps {
  card: Card;
  onCardClick: (card: Card) => void;
  getCardKey?: (card: Card) => string;
  index: number;
  renderCardStats: (card: Card) => React.ReactNode;
  currency: Currency;
  finish?: CardFinish;
  availableFinishes?: CardFinish[] | null;
}

type ViewMode = 'grid' | 'table' | 'binder';
type SortField = 'number' | 'name' | 'price' | 'rarity';
type SortDirection = 'asc' | 'desc';
type PokemonType = 'All' | 'Colorless' | 'Darkness' | 'Dragon' | 'Fighting' | 'Fire' | 'Grass' | 'Lightning' | 'Metal' | 'Psychic' | 'Water';

const VIEW_MODE_KEY = 'pokecardindex_view_mode';
const SORT_FIELD_KEY = 'pokecardindex_sort_field';
const SORT_DIRECTION_KEY = 'pokecardindex_sort_direction';
const TYPE_FILTER_KEY = 'pokecardindex_type_filter';
const GRID_GAP = 'gap-1.5 xs:gap-2 sm:gap-3';

const formatRarity = (rarity: string) => {
  const abbreviations: Record<string, string> = {
    'Special Illustration Rare': 'S.I. Rare',
    'Illustration Rare': 'I. Rare',
    'Ultra Rare': 'U. Rare',
    'Secret Rare': 'S. Rare',
    'Rare Holo': 'Holo',
    'Rare Holo V': 'V',
    'Rare Holo VMAX': 'VMAX',
    'Rare Holo VSTAR': 'VSTAR',
    'Radiant Rare': 'Radiant',
  };

  return abbreviations[rarity] || rarity;
};

const CardWithObserver: React.FC<CardObserverProps> = ({ 
  card, 
  onCardClick, 
  getCardKey, 
  index, 
  renderCardStats, 
  currency,
  finish,
  availableFinishes
}) => {
  const [addedCardId, setAddedCardId] = useState<string | null>(null); 
  const { currency: currentCurrency } = useCurrencyStore();

  useEffect(() => {
    const handleCardAdded = (e: CustomEvent) => {
      if (e.detail.cardId === card.id) {
        setAddedCardId(e.detail.cardId);
        setTimeout(() => setAddedCardId(null), 800);
      }
    };

    document.addEventListener('cardAdded', handleCardAdded as EventListener);
    return () => document.removeEventListener('cardAdded', handleCardAdded as EventListener);
  }, [card.id]);

  if (!card) return null;

  return (
    <div 
      key={card.uniqueId || getCardKey?.(card)}
      className={`group relative bg-black/40 backdrop-blur-sm border rounded-lg overflow-hidden text-left transition-all duration-300 ease-out shadow-lg ${
        addedCardId === card.id 
          ? 'border-green-500 ring-4 ring-green-500/20 scale-105 z-10 shadow-2xl shadow-green-500/20' 
          : 'border-white/10 hover:border-white/30 hover:shadow-xl'
      }`}
      onClick={() => onCardClick(card)}
    >
      <div className="relative aspect-[2.5/3.5]">
        <CardImage 
          src={card.images.small}
          alt={card.name}
          cardId={card.id}
          className="absolute inset-0 w-full h-full object-cover"
          width={245}
          quality={60}
        />
      </div>

      <div className="p-3 border-t border-white/10 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-medium text-white truncate max-w-[75%]" title={card.name}>
            {card.name}
          </h3>
          <span className="text-xs text-white/60 flex-shrink-0">
            {card.number}/{card.set.printedTotal}
          </span>
        </div>
        
        <div className="flex flex-col gap-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            {card.rarity && (
              <span className="px-2 py-0.5 bg-white/10 rounded-full text-white/80" title={card.rarity}>
                {formatRarity(card.rarity)}
              </span>
            )}
            {renderCardStats(card)}
          </div>
          <div className="font-medium text-white whitespace-nowrap overflow-hidden text-ellipsis">
            {formatPrice(getCardPrice(card), currentCurrency)}
          </div>
        </div>
        
        <div className="flex justify-end mt-1">
          <CollectionButton card={card} defaultFinish={finish} availableFinishes={availableFinishes} />
        </div>
      </div>
    </div>
  );
}

export default function CardGrid({ 
  cards, 
  onCardClick, 
  activeFilter, 
  getCardKey = (card) => card.id, 
  loading = false,
  isCollectionView = false,
  enablePagination = false,
  cardsPerPage = 250
}: CardGridProps) {
  // Use different localStorage keys for collection views
  const storagePrefix = isCollectionView ? 'collection_' : '';
  const viewModeKey = `${storagePrefix}${VIEW_MODE_KEY}`;
  const sortFieldKey = `${storagePrefix}${SORT_FIELD_KEY}`;
  const sortDirectionKey = `${storagePrefix}${SORT_DIRECTION_KEY}`;
  const typeFilterKey = `${storagePrefix}${TYPE_FILTER_KEY}`;

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      return (localStorage.getItem(viewModeKey) as ViewMode) || 'grid';
    } catch {
      return 'grid';
    }
  });
  const [sortField, setSortField] = useState<SortField>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedType, setSelectedType] = useState<PokemonType>(() => {
    try {
      return (localStorage.getItem(typeFilterKey) as PokemonType) || 'All';
    } catch {
      return 'All';
    }
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(cardsPerPage);
  
  // Force re-render key when sorting changes


  const typeFilteredCards = useMemo(() => {
    if (selectedType === 'All') {
      return cards;
    }
    return cards.filter(card => card.types?.includes(selectedType));
  }, [selectedType, cards]);

  const sortedCards = useMemo(() => {
    const sorted = [...typeFilteredCards].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'number':
          // Extract numeric part from card numbers (e.g., "35/" -> 35, "16/" -> 16)
          const numA = parseInt(a.number.replace(/[^0-9]/g, '')) || 0;
          const numB = parseInt(b.number.replace(/[^0-9]/g, '')) || 0;
          comparison = numA - numB;
          // If numbers are the same, sort by name as secondary criteria
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          // If names are the same, sort by number as secondary criteria
          if (comparison === 0) {
            const numA = parseInt(a.number.replace(/[^0-9]/g, '')) || 0;
            const numB = parseInt(b.number.replace(/[^0-9]/g, '')) || 0;
            comparison = numA - numB;
          }
          break;
        case 'price':
          const priceA = getCardPrice(a);
          const priceB = getCardPrice(b);
          comparison = priceA - priceB;
          

          
          // If prices are the same, sort by name as secondary criteria
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case 'rarity':
          comparison = (a.rarity || '').localeCompare(b.rarity || '');
          // If rarity is the same, sort by name as secondary criteria
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [typeFilteredCards, sortField, sortDirection]);

  // Pagination logic
  const paginatedCards = useMemo(() => {
    if (!enablePagination) return sortedCards;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedCards.slice(startIndex, endIndex);
  }, [sortedCards, enablePagination, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);

  const cardIds = useMemo(() => paginatedCards.map(c => c.id), [paginatedCards]);
  const finishMap = useBatchCardFinishes(cardIds);

  const { currency } = useCurrencyStore();
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    try {
      localStorage.setItem(viewModeKey, viewMode);
    } catch (error) {
      console.error('Error saving view mode:', error);
    }
  }, [viewMode, viewModeKey]);

  useEffect(() => {
    try {
      localStorage.setItem(sortFieldKey, sortField);
      localStorage.setItem(sortDirectionKey, sortDirection);
    } catch (error) {
      console.error('Error saving sort preferences:', error);
    }
  }, [sortField, sortDirection, sortFieldKey, sortDirectionKey]);

  useEffect(() => {
    try {
      localStorage.setItem(typeFilterKey, selectedType);
    } catch (error) {
      console.error('Error saving type filter:', error);
    }
    // Reset to first page when type filter changes
    setCurrentPage(1);
  }, [selectedType, typeFilterKey]);

  // Memoize the sort function to prevent unnecessary re-renders
  const handleSort = useCallback((field: 'number' | 'name' | 'price' | 'rarity') => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  // Use useCallback for pagination handlers to prevent unnecessary re-renders
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    // Scroll to top when changing page size
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const SortButton = ({ field, icon, label }: { field: SortField, icon: React.ReactNode, label: string }) => (
    <button
      onClick={() => handleSort(field)}
      className={`px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors flex items-center gap-2 ${
        sortField === field ? 'bg-white/10 text-white' : 'bg-white/5 text-white/60'
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {sortField === field && (
        <span className="text-white/60">
          {sortDirection === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
        </span>
      )}
    </button>
  );

  const getHighestAttackDamage = (card: Card): number => {
    if (!card.attacks) return 0;
    return Math.max(...card.attacks.map(attack => {
      const damage = parseInt(attack.damage?.replace(/[^0-9]/g, '') || '0');
      return isNaN(damage) ? 0 : damage;
    }));
  };

  const renderCardStats = (card: Card) => (
    <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
      {card.hp && (
        <div className="flex items-center gap-1">
          <HPIcon className="text-red-500" size={12} />
          <span>{card.hp}</span>
        </div>
      )}
      {getHighestAttackDamage(card) > 0 && (
        <div className="flex items-center gap-1">
          <Sword size={12} className="text-red-500" />
          <span>{getHighestAttackDamage(card)}</span>
        </div>
      )}
      {card.weaknesses?.length > 0 && (
        <div className="flex items-center gap-1">
          <Shield size={12} className="text-purple-500" />
          {card.weaknesses[0].type && (
            <EnergyIcon type={card.weaknesses[0].type} />
          )}
          <span className="text-white/60">{card.weaknesses[0].value}</span>
        </div>
      )}
    </div>
  );

  const renderGridView = () => (
    <div className={`grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 ${GRID_GAP}`}>
      {paginatedCards.map((card, index) => (
        <CardWithObserver
          key={`grid-${card.uniqueId || getCardKey(card)}-${currentPage}-${index}`}
          card={card}
          onCardClick={onCardClick}
          getCardKey={getCardKey}
          index={index}
          renderCardStats={renderCardStats}
          currency={currency}
          finish={card.finish}
          availableFinishes={finishMap.get(card.id) ?? null}
        />
      ))}
    </div>
  );

  const renderTableView = () => (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <table className="w-full text-left text-sm text-white">
        <thead className="bg-black/95 backdrop-blur-sm text-xs uppercase sticky top-0 z-10">
          <tr>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3">Card</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">Number</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3">Name</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3 hidden md:table-cell">Type</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">Rarity</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3 hidden xl:table-cell">Stats</th>
            <th className="px-1.5 sm:px-4 py-2 sm:py-3 min-w-[100px]">Price</th>
          </tr>
        </thead>
        <tbody>
          {paginatedCards.map((card, index) => (
            <tr 
              key={`table-${card.uniqueId || getCardKey(card)}-${currentPage}-${index}`}
              className="border-b border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer"
              onClick={() => onCardClick(card)}
            >
              <td className="px-2 sm:px-4 py-2 w-16 sm:w-20">
                <div className="relative w-12 sm:w-16 aspect-[2.5/3.5]">
                  <CardImage
                    src={card.images.small}
                    alt={card.name}
                    cardId={card.id}
                    className="absolute inset-0 w-full h-full object-cover rounded"
                    width={120}
                    quality={75}
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>
              </td>
              <td className="px-2 sm:px-4 py-2 hidden sm:table-cell">{card.number}/{card.set.printedTotal}</td>
              <td className="px-2 sm:px-4 py-2 font-medium max-w-[120px] sm:max-w-none truncate">{card.name}</td>
              <td className="px-2 sm:px-4 py-2 hidden md:table-cell">
                {card.types?.[0] && <EnergyIcon type={card.types[0]} />}
              </td>
              <td className="px-2 sm:px-4 py-2 hidden lg:table-cell">{formatRarity(card.rarity)}</td>
              <td className="px-2 sm:px-4 py-2 hidden xl:table-cell">{renderCardStats(card)}</td>
              <td className="px-2 sm:px-4 py-2 text-right sm:text-left whitespace-nowrap">
                {formatPrice(getCardPrice(card), currency)}
                <div className="mt-1 flex justify-end">
                  <CollectionButton card={card} finish={card.finish} availableFinishes={finishMap.get(card.id) ?? null} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderBinderView = () => (
    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-1 bg-white/5 p-1 rounded-lg">
      {paginatedCards.map((card, index) => (
        <article
          key={`binder-${card.uniqueId || getCardKey(card)}-${currentPage}-${index}`}
          onClick={() => onCardClick(card)}
          className="relative aspect-[2.5/3.5] group cursor-pointer overflow-hidden"
        >
          <CardImage
            src={card.images.small}
            alt={card.name}
            cardId={card.id}
            quality={60}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
            <div className="absolute bottom-0 inset-x-0 p-3">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <p className="text-sm font-medium text-white truncate">{card.name}</p>
                  <p className="text-xs text-white/60">
                    {card.number}/{card.set.printedTotal}
                  </p>
                </div>
                {card.types?.[0] && (
                  <EnergyIcon type={card.types[0]} />
                )}
              </div>
              <p className="text-sm font-medium text-white/80">
                {formatPrice(getCardPrice(card), currency)}
              </p>
              <div className="mt-2 flex justify-center">
                <CollectionButton card={card} finish={card.finish} availableFinishes={finishMap.get(card.id) ?? null} />
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );

  return (
    <div>
      <div className="border-b border-white/10 px-4 sm:px-6 lg:px-8 py-2 sm:py-4">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 relative">
              <button
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base ${
                  (selectedType !== 'All' || (activeFilter && activeFilter !== 'All')) ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'
                }`}
                onClick={(e) => {
                  const dropdown = document.getElementById('type-dropdown');
                  if (dropdown) {
                    dropdown.classList.toggle('hidden');
                  }
                }}
              >
                {selectedType === 'All' && (!activeFilter || activeFilter === 'All') ? (
                  <>
                    <Zap size={16} />
                    <span className="hidden sm:inline">Type</span>
                  </>
                ) : (
                  <>
                    <EnergyIcon type={activeFilter || selectedType} size={16} />
                    <span className="hidden sm:inline">{activeFilter || selectedType}</span>
                    {activeFilter && activeFilter !== 'All' && (
                      <span className="text-xs ml-1">({cards.length})</span>
                    )}
                  </>
                )}
              </button>
              <div 
                id="type-dropdown"
                className="hidden absolute z-50 top-full left-0 mt-1 bg-black/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg py-1 min-w-[120px]"
              >
                {(['All', 'Colorless', 'Darkness', 'Dragon', 'Fighting', 'Fire', 'Grass', 'Lightning', 'Metal', 'Psychic', 'Water'] as PokemonType[]).map(type => (
                  <button
                    key={type}
                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-white/10 ${
                      selectedType === type ? 'bg-white/10 text-white' : 'text-white/60'
                    }`}
                    onClick={() => {
                      setSelectedType(type);
                      document.getElementById('type-dropdown')?.classList.add('hidden');
                    }}
                  >
                    {type === 'All' ? (
                      <Zap size={16} />
                    ) : (
                      <EnergyIcon type={type} size={16} />
                    )}
                    {type}
                  </button>
                ))}
              </div>

              <SortButton field="number" icon={<Hash size={16} />} label="Number" />
              <SortButton field="name" icon={<ArrowUpDown size={16} />} label="Name" />
              <SortButton field="price" icon={<DollarSign size={16} />} label="Price" />
              <SortButton field="rarity" icon={<Sparkles size={16} />} label="Rarity" />
            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base ${
                  viewMode === 'grid' ? 'bg-white/20 text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                <Grid size={16} />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base ${
                  viewMode === 'table' ? 'bg-white/20 text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                <List size={16} />
                <span className="hidden sm:inline">Table</span>
              </button>
              <button
                onClick={() => setViewMode('binder')}
                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base ${
                  viewMode === 'binder' ? 'bg-white/20 text-white font-medium' : 'text-white/60 hover:text-white'
                }`}
              >
                <Book size={16} />
                <span className="hidden sm:inline">Binder</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1600px] mx-auto">
          {/* Pagination Controls - Top */}
          {enablePagination && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-black/95 border border-white/10 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-4">
                <span className="text-sm text-white/60">
                  Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedCards.length)} of {sortedCards.length} cards
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handlePerPageChange(Number(e.target.value))}
                  className="bg-gray-800 border border-white/20 rounded-lg px-3 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
                  style={{
                    colorScheme: 'dark'
                  }}
                >
                  <option value={100} className="bg-gray-800 text-white">100 per page</option>
                  <option value={250} className="bg-gray-800 text-white">250 per page</option>
                  <option value={500} className="bg-gray-800 text-white">500 per page</option>
                  <option value={1000} className="bg-gray-800 text-white">1000 per page</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Previous
                </button>
                
                <span className="px-3 py-1.5 text-sm text-white/60">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Last
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <CardGridSkeleton />
          ) : (
            <>
              {viewMode === 'grid' && renderGridView()}
              {viewMode === 'table' && renderTableView()}
              {viewMode === 'binder' && renderBinderView()}
            </>
          )}

          {/* Pagination Controls - Bottom */}
          {enablePagination && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-black/95 border border-white/10 rounded-lg p-4 mt-6">
              <div className="text-sm text-white/60">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedCards.length)} of {sortedCards.length} cards
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Previous
                </button>
                
                <span className="px-3 py-1.5 text-sm text-white/60">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}